import { motion } from 'framer-motion'
import { Buildings } from '@phosphor-icons/react'
import { useEntityDetails } from '../hooks/useEntityDetails'
import { useAccount } from 'wagmi'

interface EntitySelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (entityAddress: string) => void
  productName: string
}

export function EntitySelectionModal({ isOpen, onClose, onSelect, productName }: EntitySelectionModalProps) {
  const { address } = useAccount()
  const { entities } = useEntityDetails()

  // Filter entities owned by the current user
  const userEntities = entities?.filter(entity => 
    entity.owner.toLowerCase() === address?.toLowerCase()
  ) || []

  if (!isOpen) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gradient-to-br from-amber-500/20 via-yellow-500/20 to-orange-500/20 rounded-2xl p-8 max-w-md w-full mx-4"
      >
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-white mb-2">Select Entity</h3>
          <p className="text-white/60">
            Choose an entity to purchase {productName}
          </p>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {userEntities.length === 0 ? (
            <div className="text-center text-white/60 py-8">
              You don't own any entities yet
            </div>
          ) : (
            userEntities.map(entity => (
              <motion.button
                key={entity.address}
                onClick={() => onSelect(entity.address)}
                className="w-full bg-gradient-to-br from-amber-500/20 via-yellow-500/20 to-orange-500/20 rounded-xl p-4 text-left hover:from-amber-500/30 hover:via-yellow-500/30 hover:to-orange-500/30 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg flex items-center justify-center">
                      <Buildings size={24} className="text-white/60" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">{entity.name}</h4>
                    <p className="text-white/60 text-sm">{entity.description}</p>
                  </div>
                </div>
              </motion.button>
            ))
          )}
        </div>

        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
} 